import React, { useState, useEffect } from 'react'
import { Button, Grid, CircularProgress } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import './live-data-view-style.scss'

// Import status icons
import DS1 from '../../../../assets/images/DS1.svg' // Yellow - LIVE_SYM
import DS2 from '../../../../assets/images/DS2.svg' // Green - Connected
import DS3 from '../../../../assets/images/DS3.svg' // Red - Error

// Import API functions
import {
  FetchLiveDeviceData,
  FetchCalculatedData,
  FetchAllLiveDeviceData,
  FetchAllCalculatedData,
  FetchDeviceStatusNew,
  FetchCalculatedInvertersForPlant,
  FetchAllInvertersLiveData,
} from '../../../../services/api'

const LiveDataView = ({ plantData, token }) => {
  // SECTION 2: FETCHED / DIRECT DATA (appears first)
  // Data fetched directly from backend/IoT API with no frontend calculations
  const [fetchedData, setFetchedData] = useState({
    inverters: [], // Array to hold multiple inverters
    sensor: {
      sensorID: 'SNS-001',
      statusIcon: DS3,
      status: 'OFFLINE',
      modelName: '—',
      name: '—',
      temperature: { value: '—', unit: '°C' },
      irradiance: { value: '—', unit: 'W/m²' },
      irradiation: { value: '—', unit: 'kWh/m²' },
    },
  })

  // SECTION 1: CALCULATED / ANALYSIS DATA (appears second)
  // Data calculated or derived in frontend using fetched values
  const [calculatedData, setCalculatedData] = useState({
    meter: {
      meterID: 'MTR-001',
      statusIcon: DS1,
      status: 'OFFLINE',
      lastEnergyExport: { value: '—', unit: 'kWh' },
      current: { value: '—', unit: 'kWh' },
      multiplyingFactor: { value: '1', unit: 'x' },
      todayGeneration: { value: '—', unit: 'kWh' },
      name: '—',
    },
  })

  // Dynamic calculated inverters array
  const [calculatedInverters, setCalculatedInverters] = useState([])

  const [loading, setLoading] = useState(false)
  const [deviceStatus, setDeviceStatus] = useState({})

  // Helper function to get status icon based on device status
  const getStatusIcon = (status) => {
    if (!status) return DS3 // Red - offline by default
    if (status.toLowerCase() === 'online' || status.toLowerCase() === 'live') {
      return DS2 // Green - online
    }
    return DS1 // Yellow - other status
  }

  // Helper function to format value with fallback
  const formatValue = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '—') return '—'
    const num = parseFloat(value)
    if (isNaN(num)) return '—'
    return num.toFixed(decimals)
  }

  // Fetch device status for all devices
  const fetchDeviceStatus = async () => {
    if (!plantData?.id || !token) return null

    try {
      const response = await FetchDeviceStatusNew(plantData.id, token)
      // Handle new API response structure from /api/plant/device-status-v2/
      if (response?.data?.status && response.data.data) {
        const statusMap = {}
        
        // Map individual device statuses
        // If overallDeviceStatus is 'green', devices are online; otherwise offline
        const isOnline = response.data.data.overallDeviceStatus === 'green' || response.data.data.status === 'All Online'
        const deviceStatus = isOnline ? 'ONLINE' : 'OFFLINE'
        
        // Map device IDs to their status
        statusMap['INV-001'] = deviceStatus
        statusMap['INV-002'] = deviceStatus
        statusMap['MTR-001'] = deviceStatus
        statusMap['SNS-001'] = deviceStatus
        
        setDeviceStatus(statusMap)
        return statusMap  // Return the status map for immediate use
      }
    } catch (error) {
      console.error('Error fetching device status:', error)
      return null
    }
  }

  // Fetch fetched live data (real-time data)
  const loadFetchedData = async (statusMap) => {
    if (!plantData?.id || !token) return

    try {
      // Fetch all inverters data using new ALL-INVERTERS endpoint
      const invertersResponse = await FetchAllInvertersLiveData(
        plantData.id,
        token
      )
      if (invertersResponse?.success && invertersResponse.data?.inverters) {
        const invertersArray = invertersResponse.data.inverters
        
        // Transform inverters data to match FetchedInverterCard props
        const transformedInverters = invertersArray.map((inverter) => {
          const inverterStatus = statusMap?.[inverter.deviceId] || 'OFFLINE'
          
          return {
            inverterID: inverter.deviceId,
            channel: inverter.channel,
            statusIcon: getStatusIcon(inverterStatus),
            status: inverterStatus,
            name: inverter.name || 'Inverter',
            model: inverter.model || '—',
            make: inverter.make || '—',
            capacity: inverter.capacity || '—',
            power: {
              value: formatValue(inverter.power),
              unit: 'kW',
            },
            todayGeneration: {
              value: formatValue(inverter.todayGeneration),
              unit: 'kWh',
            },
          }
        })
        
        setFetchedData((prev) => ({
          ...prev,
          inverters: transformedInverters,
        }))
      }

      // Fetch Sensor data
      const sensorResponse = await FetchLiveDeviceData(
        plantData.id,
        'SNS-001',
        token
      )
      if (sensorResponse?.success && sensorResponse.data) {
        const sensorStatus = statusMap?.['SNS-001'] || 'OFFLINE'
        setFetchedData((prev) => ({
          ...prev,
          sensor: {
            ...prev.sensor,
            statusIcon: getStatusIcon(sensorStatus),
            status: sensorStatus,
            name: sensorResponse.data.name || 'Weather Sensor',
            modelName: sensorResponse.data.model || '—',
            temperature: {
              value: formatValue(sensorResponse.data.temperature),
              unit: '°C',
            },
            irradiance: {
              value: formatValue(sensorResponse.data.irradiance),
              unit: 'W/m²',
            },
            irradiation: {
              value: formatValue(sensorResponse.data.irradiation),
              unit: 'kWh/m²',
            },
          },
        }))
      }
    } catch (error) {
      console.error('Error loading fetched data:', error)
    }
  }

  // Fetch calculated data (historical comparison)
  const loadCalculatedData = async (statusMap) => {
    if (!plantData?.id || !token) return

    try {
      // Fetch Energy Meter calculated data
      const meterResponse = await FetchCalculatedData(
        plantData.id,
        'MTR-001',
        token
      )
      if (meterResponse?.success && meterResponse.data) {
        // Default multiplying factor is 1
        const multiplyingFactor = meterResponse.data.multiplyingFactor || 1
        
        // Calculate today's generation: (current - last) × multiplying factor
        const lastExport = parseFloat(meterResponse.data.lastEnergyMeterTillExport) || 0
        const currentExport = parseFloat(meterResponse.data.currentEnergyMeterTillExport) || 0
        const difference = currentExport - lastExport
        const todayGeneration = difference * multiplyingFactor
        
        const mtrStatus = statusMap?.['MTR-001'] || 'OFFLINE'
        setCalculatedData((prev) => ({
          ...prev,
          meter: {
            ...prev.meter,
            name: meterResponse.data.deviceName || 'Main Energy Meter',
            statusIcon: getStatusIcon(mtrStatus),
            status: mtrStatus,
            lastEnergyExport: {
              value: formatValue(lastExport),
              unit: 'kWh',
            },
            current: {
              value: formatValue(currentExport),
              unit: 'kWh',
            },
            multiplyingFactor: {
              value: formatValue(multiplyingFactor, 1),
              unit: 'x',
            },
            todayGeneration: {
              value: formatValue(todayGeneration),
              unit: 'kWh',
            },
          },
        }))
      }
    } catch (error) {
      console.error('Error loading calculated data:', error)
    }
  }

  // Fetch calculated inverters for the plant
  const loadCalculatedInverters = async (statusMap) => {
    if (!plantData?.id || !token) return

    try {
      const response = await FetchCalculatedInvertersForPlant(
        plantData.id,
        token
      )
      
      console.log('loadCalculatedInverters response:', response)
      
      if (response?.success && response.data && response.data.inverters) {
        const invertersArray = response.data.inverters
        
        console.log('invertersArray:', invertersArray)
        
        // Transform inverters data to match CalculatedInverterCard props
        const transformedInverters = invertersArray.map((inverter) => {
          // Calculate today's generation: current - last
          const lastEnergy = parseFloat(inverter.lastTotalEnergy) || 0
          const currentEnergy = parseFloat(inverter.currentTotalEnergy) || 0
          const todayGen = currentEnergy - lastEnergy
          
          // Get status from statusMap using deviceId
          const inverterStatus = statusMap?.[inverter.deviceId] || 'OFFLINE'
          
          return {
            inverterID: inverter.deviceId,
            channel: inverter.channel,
            statusIcon: getStatusIcon(inverterStatus),
            status: inverterStatus,
            last: {
              value: formatValue(lastEnergy),
              unit: 'kWh',
            },
            current: {
              value: formatValue(currentEnergy),
              unit: 'kWh',
            },
            todayGeneration: {
              value: formatValue(todayGen),
              unit: 'kWh',
            },
            model: inverter.model || '—',
            name: inverter.deviceName || 'Inverter',
          }
        })
        
        console.log('transformedInverters:', transformedInverters)
        
        setCalculatedInverters(transformedInverters)
      } else {
        // If API doesn't return inverters, set to empty array
        console.log('No inverters in response')
        setCalculatedInverters([])
      }
    } catch (error) {
      console.error('Error loading calculated inverters:', error)
      // On error, set to empty array to maintain UI consistency
      setCalculatedInverters([])
    }
  }

  // Initial load and refresh function
  const handleRefresh = async () => {
    setLoading(true)
    try {
      // IMPORTANT: Fetch device status FIRST, then other data
      // This ensures statusMap is populated before getStatusIcon() is called
      const statusMap = await fetchDeviceStatus()
      // Pass statusMap to other functions for synchronous use
      await Promise.all([
        loadFetchedData(statusMap),
        loadCalculatedData(statusMap),
        loadCalculatedInverters(statusMap),
      ])
    } catch (error) {
      console.error('Error refreshing live data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Load data on component mount
  useEffect(() => {
    if (plantData?.id && token) {
      // Initial load
      handleRefresh()

      // Set up 5-minute interval refresh (300000 ms = 5 minutes)
      const intervalId = setInterval(() => {
        handleRefresh()
      }, 300000)

      // Cleanup interval on component unmount
      return () => clearInterval(intervalId)
    }
  }, [plantData?.id, token])

  // ==================== COMPONENT STRUCTURE ====================

  // Reusable Data Row Component - displays label and value pair
  const DataRow = ({ label, value, unit }) => (
    <div className="data-row">
      <span className="data-label">{label}</span>
      <span className="data-value">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </span>
    </div>
  )

  // Reusable Data Card Component - displays a device (inverter/sensor/meter)
  const DataCard = ({ title, subtitle, statusIcon, statusText, children }) => (
    <div className="data-card">
      <div className="card-header">
        <div>
          <h3 className="card-title">{title}</h3>
          {subtitle && <p className="card-subtitle">{subtitle}</p>}
        </div>
        {statusIcon && (
          <div className="card-status">
            <img src={statusIcon} alt="status" className="status-icon" />
            {statusText && <span className="status-text">{statusText}</span>}
          </div>
        )}
      </div>
      <div className="card-body">{children}</div>
    </div>
  )

  // SECTION 2: Fetched Data Card Components
  const FetchedInverterCard = ({ data }) => (
    <DataCard
      title={data.inverterID}
      subtitle={data.name}
      statusIcon={data.statusIcon}
      statusText={data.status}
    >
      {data.model && <DataRow label="Model" value={data.model} />}
      <DataRow
        label="Power"
        value={data.power.value}
        unit={` ${data.power.unit}`}
      />
      <DataRow label="Status" value={data.status} />
      <DataRow
        label="Today Generation"
        value={data.todayGeneration.value}
        unit={` ${data.todayGeneration.unit}`}
      />
    </DataCard>
  )

  const FetchedSensorCard = ({ data }) => (
    <DataCard
      title={data.sensorID}
      subtitle={data.name}
      statusIcon={data.statusIcon}
      statusText={data.status}
    >
      <DataRow label="Model Name" value={data.modelName} />
      <DataRow
        label="Temperature"
        value={data.temperature.value}
        unit={` ${data.temperature.unit}`}
      />
      <DataRow
        label="Irradiance"
        value={data.irradiance.value}
        unit={` ${data.irradiance.unit}`}
      />
      <DataRow
        label="Irradiation"
        value={data.irradiation.value}
        unit={` ${data.irradiation.unit}`}
      />
    </DataCard>
  )

  // SECTION 1: Calculated Data Card Components
  const CalculatedMeterCard = ({ data }) => (
    <DataCard
      title={data.meterID}
      subtitle={data.name}
      statusIcon={data.statusIcon}
      statusText={data.status}
    >
      <DataRow label="Status" value={data.status} />
      <DataRow
        label="Last Energy Export Till"
        value={data.lastEnergyExport.value}
        unit={` ${data.lastEnergyExport.unit}`}
      />
      <DataRow
        label="Current Energy Export Till"
        value={data.current.value}
        unit={` ${data.current.unit}`}
      />
      <DataRow
        label="Multiplying Factor"
        value={data.multiplyingFactor.value}
        unit={` ${data.multiplyingFactor.unit}`}
      />
      <DataRow
        label="Today Generation"
        value={data.todayGeneration.value}
        unit={` ${data.todayGeneration.unit}`}
      />
    </DataCard>
  )

  const CalculatedInverterCard = ({ data }) => (
    <DataCard
      title={data.inverterID}
      subtitle={data.name}
      statusIcon={data.statusIcon}
      statusText={data.status}
    >
      {data.model && <DataRow label="Model" value={data.model} />}
      <DataRow label="Status" value={data.status} />
      <DataRow
        label="Last"
        value={data.last.value}
        unit={` ${data.last.unit}`}
      />
      <DataRow
        label="Current"
        value={data.current.value}
        unit={` ${data.current.unit}`}
      />
      <DataRow
        label="Today Generation"
        value={data.todayGeneration.value}
        unit={` ${data.todayGeneration.unit}`}
      />
    </DataCard>
  )

  return (
    <div className="live-data-view-container">
      {/* ==================== MAIN CONTENT ==================== */}
      <div className="live-data-content">
        {/* Loading Overlay */}
        {loading && (
          <div className="loading-overlay">
            <CircularProgress size={40} />
            <span>Updating live data...</span>
          </div>
        )}

        {/* ========== SECTION 2: FETCHED / DIRECT DATA (APPEARS FIRST) ========== */}
        <section className="data-section fetched-section">
          <div className="section-header space-between space-after-section">
            <div>
              <h2 className="section-title">Fetched (Real-Time Data)</h2>
              <p className="section-description">
                Latest device readings from IoT sensors
              </p>
            </div>
            <div className="section-actions">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                className="refresh-button"
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>

          <div className="section-grid">
            {/* Inverters Grid - Responsive layout for multiple inverters */}
            <Grid container spacing={2}>
              {fetchedData.inverters.length > 0 ? (
                fetchedData.inverters.map((inverter) => (
                  <Grid item xs={12} sm={6} key={inverter.inverterID}>
                    <FetchedInverterCard data={inverter} />
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <div className="data-card no-data">
                    <p>No inverter data available</p>
                  </div>
                </Grid>
              )}
              {/* Sensor on the side - takes remaining space */}
              {fetchedData.inverters.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <FetchedSensorCard data={fetchedData.sensor} />
                </Grid>
              )}
              {/* If no inverters, show sensor below */}
              {fetchedData.inverters.length === 0 && (
                <Grid item xs={12} sm={6}>
                  <FetchedSensorCard data={fetchedData.sensor} />
                </Grid>
              )}
            </Grid>
          </div>
        </section>

        {/* ========== SECTION 1: CALCULATED / ANALYSIS DATA (APPEARS SECOND) ========== */}
        <section className="data-section calculated-section">
          <div className="section-header">
            <h2 className="section-title">
              Calculated (Daily Comparison)
            </h2>
            <p className="section-description">
              Previous day vs current day energy values
            </p>
          </div>

          <div className="section-grid">
            {/* Meter 1 & Dynamic Inverters - Responsive layout */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <CalculatedMeterCard data={calculatedData.meter} />
              </Grid>
              {calculatedInverters.map((inverter) => (
                <Grid item xs={12} sm={6} key={inverter.inverterID}>
                  <CalculatedInverterCard data={inverter} />
                </Grid>
              ))}
            </Grid>
          </div>
        </section>
      </div>
    </div>
  )
}

export default LiveDataView
